import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { DialogService } from '../../../utility/dialog/dialog-service';
import { EditDialogService } from '../../../utility/dialog/edit-dialog-service';
import { HttpService } from '../../../utility/http-service';
import { LoginService } from '../../../../services/login-service';
import { RouterService } from '../../../utility/router-service';
import { GenericDragSelectComponent } from '../../generic-drag-select/generic-drag-select.component';
import { RegionSelectModel } from '../../../pages/client/client.component';
import { GridContextMenuItem, WcGridComponent } from '../wc-grid.component';

@Component({
  selector: 'wc-grid-popup',
  standalone: false,
  templateUrl: './wc-grid-popup.component.html',
  styleUrl: '../grid-theme.css'
})
export class WcGridPopupComponent {
  public gridKey!: string;
  public showImpersonation = false;
  public selectedRow: any = null;
  public context: Record<string, any> = {};
  public contextMenuItems: GridContextMenuItem[] = [];
  public onAction?: (id: string, row: Record<string, any>) => void;

  @ViewChild(WcGridComponent) grid?: WcGridComponent;

  // Grids that get a free-text quick-search box.
  private static readonly SEARCHABLE_GRIDS = new Set([
    'fracxClientMgmt', 'fracxUserMgmt', 'aquatradeClientMgmt', 'aquatradeUserMgmt', 'fracContacts'
  ]);

  get showQuickSearch(): boolean {
    return WcGridPopupComponent.SEARCHABLE_GRIDS.has(this.gridKey);
  }

  get searchPlaceholder(): string {
    if (this.gridKey.toLowerCase().includes('user')) return 'Search users...';
    if (this.gridKey === 'fracContacts') return 'Search contacts...';
    return 'Search clients...';
  }

  constructor(
    public dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public model: any,
    dialogService: DialogService,
    private editDialogService: EditDialogService,
    private http: HttpService,
    public loginService: LoginService,
    private router: RouterService
  ) {
    const value: (string | null | undefined) = model['gridKey'];
    if (!value) dialogService.showError('Invalid configuration detected - please contact well chron support');
    else this.gridKey = value;

    this.showImpersonation = !!model['showImpersonation'];
    this.context = model['context'] ?? {};
    this.contextMenuItems = model['contextMenuItems'] ?? [];
    this.onAction = model['onAction'];
  }

  onRowSelected(row: any): void {
    this.selectedRow = row;
  }

  onGridAction(event: { id: string; row: Record<string, any> }): void {
    this.onAction?.(event.id, event.row);
  }

  selectRegions(): void {
    if (!this.selectedRow) return;
    const clientId = this.selectedRow['ClientId'] ?? this.selectedRow['clientId'];
    if (!clientId) return;

    this.http.get<RegionSelectModel>(`FracX/ActiveRegions?clientId=${clientId}`, (f: RegionSelectModel) => {
      this.editDialogService.openModal(GenericDragSelectComponent, (c: MatDialogConfig) => {
        c.data = {
          title: 'Select Regions',
          current: f.current,
          available: f.available,
          onSave: (current: any[]) => {
            const formData = new FormData();
            formData.append('selectedRegions', JSON.stringify(current.map((x: any) => x.regionName ?? x)));
            formData.append('clientId', clientId);
            this.http.post('FracX/SaveRegions', () => this.grid?.reload(1), null, formData);
          }
        };
        c.minWidth = 'fit-content';
        c.minHeight = 'fit-content';
      });
    });
  }

  selectUserRegions(): void {
    if (!this.selectedRow) return;
    const userId = this.selectedRow['UserId'] ?? this.selectedRow['userId'];
    if (!userId) return;

    this.http.get<RegionSelectModel>(`FracX/ActiveUserRegions?userId=${userId}`, (f: RegionSelectModel) => {
      this.editDialogService.openModal(GenericDragSelectComponent, (c: MatDialogConfig) => {
        c.data = {
          title: 'Select User Regions',
          current: f.current,
          available: f.available,
          onSave: (current: any[]) => {
            const formData = new FormData();
            formData.append('selectedRegions', JSON.stringify(current.map((x: any) => x.regionName ?? x)));
            formData.append('userId', userId);
            this.http.post('FracX/SaveUserRegions', () => this.grid?.reload(1), null, formData);
          }
        };
        c.minWidth = 'fit-content';
        c.minHeight = 'fit-content';
      });
    });
  }

  impersonateUser(): void {
    if (!this.selectedRow) return;
    // Try all known id field names — different grids use different casings
    const userId = this.selectedRow['UserId']
      ?? this.selectedRow['userId']
      ?? this.selectedRow['Id']
      ?? this.selectedRow['id'];
    if (!userId) return;
    this.http.post(`User/Impersonate?userId=${userId}`, async () => {
      this.dialogRef.close();
      await this.loginService.reload();
      this.router.router.navigateByUrl('/');
    });
  }
}

export function OpenGrid(
  gridKey: string,
  dialogService: EditDialogService,
  width: number = 1700,
  height: number = 850,
  showImpersonation: boolean = false,
  options?: {
    context?: Record<string, any>;
    contextMenuItems?: GridContextMenuItem[];
    onAction?: (id: string, row: Record<string, any>) => void;
  }
) {
  return dialogService.openModal(WcGridPopupComponent, (c: MatDialogConfig) => {
    c.data = { gridKey, showImpersonation, ...options };
    c.minWidth = `${width}px`;
    c.minHeight = `${height}px`;
    c.maxWidth = '95vw';
    c.maxHeight = '95vh';
  });
}
